import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MessageCircle, ClipboardList, Handshake, LogOut } from "lucide-react";
import { PlayerChat } from "./PlayerChat";
import { AdventureBoard } from "./AdventureBoard";
import { PlayerInteractionsPanel } from "./PlayerInteractionsPanel";
import { useLocale } from "@/hooks/useLocale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PresenceAvatars } from "@/components/presence/PresenceAvatars";
import { usePresence } from "@/hooks/usePresence";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { notifyPlayerChange } from "@/lib/mp-notifications";

interface MultiplayerPanelProps {
  adventureId: string;
  currentUserId: string | null;
  senderName: string;
  isHost: boolean;
  players: any[];
}

export function MultiplayerPanel({ adventureId, currentUserId, senderName, isHost, players }: MultiplayerPanelProps) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [tab, setTab] = useState("chat");
  const [chatUnread, setChatUnread] = useState(0);
  const [boardUnread, setBoardUnread] = useState(0);
  const [interactionUnread, setInteractionUnread] = useState(0);

  const { otherUsers } = usePresence({
    channelName: `play-${adventureId}`,
    enabled: true,
  });

  const handleLeave = async () => {
    if (!currentUserId || isHost) return;
    const myPlayer = players.find((p) => p.user_id === currentUserId);
    if (!myPlayer) return;

    const { error } = await supabase
      .from("adventure_players")
      .delete()
      .eq("adventure_id", adventureId)
      .eq("user_id", currentUserId) as any;

    if (error) {
      toast.error(t.mp_leave_failed);
      return;
    }

    notifyPlayerChange(
      adventureId,
      players.map((p) => p.user_id),
      currentUserId,
      senderName,
      "left"
    );
    toast.success(t.mp_left_adventure);
    navigate("/");
  };

  const handleKick = async (userId: string) => {
    if (!isHost) return;
    const target = players.find((p) => p.user_id === userId);
    if (!target || target.is_host) return;

    const { error } = await supabase
      .from("adventure_players")
      .delete()
      .eq("adventure_id", adventureId)
      .eq("user_id", userId) as any;

    if (error) {
      toast.error(t.mp_kick_failed);
      return;
    }

    notifyPlayerChange(
      adventureId,
      players.map((p) => p.user_id),
      userId,
      target.character_name || "Player",
      "kicked"
    );
    toast.success(t.mp_player_kicked);
  };

  return (
    <div className="border-t border-border bg-card/50 flex flex-col" style={{ height: "280px" }}>
      <Tabs value={tab} onValueChange={setTab} className="flex flex-col h-full">
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-muted/30 h-8 px-1 shrink-0">
          <TabsTrigger value="chat" className="text-xs gap-1 h-6 px-2 data-[state=active]:bg-background">
            <MessageCircle className="w-3 h-3" />
            {t.mp_tab_chat}
            {chatUnread > 0 && (
              <Badge variant="default" className="h-4 px-1 text-[10px] min-w-[16px]">{chatUnread}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="board" className="text-xs gap-1 h-6 px-2 data-[state=active]:bg-background">
            <ClipboardList className="w-3 h-3" />
            {t.mp_tab_board}
            {boardUnread > 0 && (
              <Badge variant="default" className="h-4 px-1 text-[10px] min-w-[16px]">{boardUnread}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="interactions" className="text-xs gap-1 h-6 px-2 data-[state=active]:bg-background">
            <Handshake className="w-3 h-3" />
            {t.mp_tab_interactions}
            {interactionUnread > 0 && (
              <Badge variant="default" className="h-4 px-1 text-[10px] min-w-[16px]">{interactionUnread}</Badge>
            )}
          </TabsTrigger>

          {/* Right side: presence + leave */}
          <div className="ml-auto flex items-center gap-1.5 pr-1">
            <PresenceAvatars users={otherUsers} size="sm" maxVisible={3} />
            {!isHost && (
              <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={handleLeave} title={t.mp_leave_adventure} aria-label={t.mp_leave_adventure}>
                <LogOut className="w-3 h-3" />
              </Button>
            )}
          </div>
        </TabsList>

        <TabsContent value="chat" className="flex-1 overflow-hidden m-0">
          <PlayerChat
            adventureId={adventureId}
            currentUserId={currentUserId}
            senderName={senderName}
            embedded
            onUnreadChange={setChatUnread}
          />
        </TabsContent>
        <TabsContent value="board" className="flex-1 overflow-hidden m-0">
          <AdventureBoard
            adventureId={adventureId}
            currentUserId={currentUserId}
            senderName={senderName}
            isHost={isHost}
          />
        </TabsContent>
        <TabsContent value="interactions" className="flex-1 overflow-hidden m-0">
          <PlayerInteractionsPanel
            adventureId={adventureId}
            currentUserId={currentUserId}
            players={players}
            onUnreadChange={setInteractionUnread}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
